/**
 * Asking an unreachable hub again, by itself, with a visible countdown. The sign-in screen
 * uses it while the hub does not answer; a manual "Try again" runs the same retry at once.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Seconds to wait before each automatic retry: soon at first, then every 30 s. */
export const RETRY_DELAYS_S: readonly number[] = [5, 10, 20, 30];

/** The wait before retry number `attempt` (0 is the first). */
export function retryDelay(attempt: number): number {
  return RETRY_DELAYS_S[Math.min(Math.max(0, attempt), RETRY_DELAYS_S.length - 1)]!;
}

/** Whole seconds left until `deadline`, never below zero. */
export function secondsUntil(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

export interface AutoRetry {
  /** Seconds until the next automatic retry; null when none is planned (inactive, or retrying now). */
  secondsLeft: number | null;
  retrying: boolean;
  /** Retry now; the countdown starts again afterwards if the hub still does not answer. */
  retryNow: () => void;
}

export function useAutoRetry(active: boolean, retry: () => Promise<void> | void): AutoRetry {
  const [attempt, setAttempt] = useState(0);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [retrying, setRetrying] = useState(false);
  const retryRef = useRef(retry);
  const inFlight = useRef(false);

  useEffect(() => {
    retryRef.current = retry;
  }, [retry]);

  const run = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRetrying(true);
    setDeadline(null);
    try {
      await retryRef.current();
    } catch {
      // The caller's store records the failure; the countdown simply starts again.
    } finally {
      inFlight.current = false;
      setRetrying(false);
      setAttempt((a) => a + 1);
    }
  }, []);

  // The hub answered (or the screen moved on): start from the short wait next time.
  useEffect(() => {
    if (!active) {
      setAttempt(0);
      setDeadline(null);
    }
  }, [active]);

  // Plan the next retry.
  useEffect(() => {
    if (!active || retrying || deadline != null) return;
    const t = Date.now();
    setNow(t);
    setDeadline(t + retryDelay(attempt) * 1000);
  }, [active, retrying, deadline, attempt]);

  // Count down, and retry when it reaches zero.
  useEffect(() => {
    if (!active || deadline == null) return;
    const tick = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= deadline) void run();
    }, 250);
    return () => clearInterval(tick);
  }, [active, deadline, run]);

  return {
    secondsLeft: active && !retrying && deadline != null ? secondsUntil(deadline, now) : null,
    retrying,
    retryNow: () => void run(),
  };
}
